import { Button, Drawer, Empty, List, Popconfirm, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Template, TemplateVersion } from "../repository/data.default";
import { getTemplatesVersions } from "../repository/data.repository";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { retry } from "../utils/http";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface TemplateHistoryDrawerProps {
  readonly open?: boolean;
  readonly template?: Template;
  readonly onClose?: () => void;
  /** Load the chosen version's Subject/Body into the edit form (saving snapshots a new version). */
  readonly onRestore?: (version: TemplateVersion) => void;
}

/**
 * Read-only timeline of a template's prior versions (snapshotted server-side on every edit), with a
 * Restore that loads a past Subject/Body back into the edit form. No new backend — reuses the
 * existing TemplatesVersions read endpoint.
 */
function TemplateHistoryDrawer(props: TemplateHistoryDrawerProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);

  const canRestore = permissions.can("Templates", "edit");

  useEffect(() => {
    if (!props.open || !props.template?.uid) {
      setVersions([]);
      return;
    }
    let active = true;
    setLoading(true);
    retry(() =>
      getTemplatesVersions({
        searchQuery: {
          conditions: [
            {
              field: nameOf<TemplateVersion>("templateUid"),
              operation: QueryOperation.Equal,
              values: [props.template!.uid],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
        sortBy: nameOf<TemplateVersion>("version"),
        sortDescending: true,
      }),
    ).then(({ data }) => {
      if (active) {
        setVersions(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [props.open, props.template?.uid]);

  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      width={480}
      title={
        <span>
          Version history
          {props.template?.name ? (
            <Typography.Text type="secondary"> — {props.template.name}</Typography.Text>
          ) : null}
        </span>
      }
    >
      <List
        loading={loading}
        dataSource={versions}
        locale={{
          emptyText: (
            <Empty description="No earlier versions yet — this template hasn't been edited." />
          ),
        }}
        renderItem={(version) => (
          <List.Item
            actions={[
              <Popconfirm
                key="restore"
                title="Restore this version?"
                description="Loads its Subject and Body into the editor. Saving creates a new version."
                okText="Restore"
                onConfirm={() => props.onRestore?.(version)}
                disabled={!canRestore}
              >
                <Button type="link" size="small" disabled={!canRestore}>
                  Restore
                </Button>
              </Popconfirm>,
            ]}
          >
            <List.Item.Meta
              title={
                <span>
                  <Tag color="default">v{version.version}</Tag>
                  <Typography.Text>{version.subject}</Typography.Text>
                </span>
              }
              description={
                <Typography.Text type="secondary" className="text-xs">
                  {dayjs(version.createdOn).format("DD/MM/YYYY HH:mm")}
                  {version.createdByUserUserName
                    ? ` · ${version.createdByUserUserName}`
                    : ""}
                </Typography.Text>
              }
            />
          </List.Item>
        )}
      />
    </Drawer>
  );
}

export default TemplateHistoryDrawer;
